import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ListIcon from '@mui/icons-material/List';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Autocomplete,
    Box,
    CircularProgress,
    Fab,
    Fade,
    LinearProgress,
    Popper,
    PopperProps,
    TextField,
    Typography,
    alpha,
} from '@mui/material';
import {
    GridCellModes,
    GridCellModesModel,
    GridCellParams,
    GridColDef,
    GridColumnVisibilityModel,
    GridEventListener,
    GridFilterModel,
    GridLogicOperator,
    GridRenderCellParams,
    GridRowGroupingModel,
    GridRowId,
    GridRowSelectionModel,
    GridValueFormatterParams,
    getGridSingleSelectOperators,
    getGridStringOperators,
    gridFilteredDescendantCountLookupSelector,
    gridStringOrNumberComparator,
    useGridApiContext,
    useGridApiRef,
} from '@mui/x-data-grid-premium';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useQueryClient, useQuery as useReactQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Switch, useHistory, useLocation, useParams } from 'react-router-dom';
import { VariableSizeList } from 'react-window';

import {
    AggregatedMalfunctionsHistory,
    ApiApi,
    CustomerUserDetails,
    DeviceV3,
    Malfunction,
    MalfunctionTypeEnum,
} from '../../../backendsdk';
import { TrackedButton as Button, TrackedDataGrid as StyledDataGrid } from '../../../components/TrackedComponents';
import { useAlert } from '../../../hooks/alert';
import useApi from '../../../hooks/api';
import useCallbackRef from '../../../hooks/callbackRef';
import useCustomer from '../../../hooks/customer';
import useDevices from '../../../hooks/devices';
import useIsMobile from '../../../hooks/isMobile';
import useProfile from '../../../hooks/profile';
import { useQuery } from '../../../hooks/query';
import { getDateTimeColumnType, localeObjectMap } from '../../../utils/DataGridDateTime';
import { flattenRoles, malfunctionsPageDefs } from '../../../utils/Pages';
import { formatLicensePlateWithInternalId, normalizeLicensePlate } from '../../../utils/Str';
import { MILLISECONDS_IN_SECOND, SECONDS_IN_MINUTE } from '../../../utils/TimeFormatter';
import {
    buildNormalizedPlateToInternalIdMap,
    getInternalIdForNormalizedPlate,
} from '../../../utils/deviceInternalIdLookup';
import { isLocationValid } from '../../../utils/location';
import palette from '../../ColorPalette';
import { DEFAULT_TITLE } from '../../Layout';
import { getUserAvatar } from '../Coaching/SessionDetails';
import { filterColumns, getColumnForNewFilter, gridLocalization } from '../OTA/MuiDeviceTable';
import OverviewMap from '../Overview/OverviewMap';
import { OE_ROLES } from '../Users/UsersComponent';
import AssignGuestTechnicianModal from './AssignGuestTechnicianModal';
import AssignmentModal from './AssignmentModal';
import DashboardLink from './DashboardLink';
import MalfunctionDetails from './MalfunctionDetails';
import MalfunctionListRow from './MalfunctionListRow';
import MalfunctionsFilter from './MalfunctionsFilter';
import MalfunctionsOverview from './MalfunctionsOverview';
import { QuickSearchToolbar } from './MalfunctionsToolbarDasktop';
import MalfunctionsToolbarMobile from './MalfunctionsToolbarMobile';
import { getMalfunctionPriority } from './PrioritzeMalfuncions';
import SetupTool from './SetupTool/SetupTool';
import ChangeVIN from './SetupTool/pages/ChangeVIN';
import { getStoredValue } from './SetupTool/utils/storage';

const GROUP_ROW_ID_STR = 'auto-generated-row';
export const GROUP_MALFUNCTIONS_KEY = 'OE-group-malfunctions';
export const PRIORITIZE_MALFUNCTION_KEY = 'OE-prioritize-malfunctions';

const CustomPopper = (props: PopperProps) => {
    return <Popper {...props} style={{ width: 'fit-content' }} placement="bottom-start" />;
};

export const RELEASED_MALFUNCTIONS = [
    MalfunctionTypeEnum.InCabinV1,
    MalfunctionTypeEnum.FrontFacing,
    MalfunctionTypeEnum.MultipleButtonPresses,
    MalfunctionTypeEnum.WrongInstallation,
    MalfunctionTypeEnum.SystemConstantlyOn,
    MalfunctionTypeEnum.PendingInstallation,
    MalfunctionTypeEnum.PendingRemoval,
    MalfunctionTypeEnum.WrongLicensePlate,
    MalfunctionTypeEnum.InactiveDevice,
    MalfunctionTypeEnum.DeviceWellness,
];

export const IS_GROUPED_BY_DEFAULT = true;

export const ALLOWED_CHANGE_VIN_CUSTOMERS = ['qa', 'tower-ny'];

export const IS_DEFAULT_PRIORITIZE = false;
export const getMalfunctions = async (api: ApiApi) => {
    const res = await api.apiV0MalfunctionGet();
    return res.data;
};

interface GridRenderWithUserOptions extends GridRenderCellParams {
    options: CustomerUserDetails[];
}

export interface MalfunctionRow {
    id: number;
    malfunction: MalfunctionTypeEnum;
    license_plate: string;
    sub_fleet: string;
    assignee: number | null;
    since: Date;
    last_updated: Date;
    comment_count: number;
    fixed: boolean;
}

interface MalfunctionDetailsWrapperProps {
    gridApiRef?: React.MutableRefObject<GridApiPremium>;
    malfunctions: Malfunction[];
    vehicleMalfunctions: Record<string, Malfunction[]>;
    setSelectionModel: CallableFunction;
    onClose: CallableFunction;
    users: CustomerUserDetails[];
    setAlert: CallableFunction;
    isLoadingUsers: boolean;
    isErrorUsers: boolean;
    setAssignToGuest: CallableFunction;
    devices: DeviceV3[];
    isMapView: boolean;
}

const MalfunctionDetailsWrapper: React.FC<MalfunctionDetailsWrapperProps> = (props) => {
    const history = useHistory();
    const { malfunctionId } = useParams<{ malfunctionId: string }>();
    const selectedMalfunction = props.malfunctions.find((m) => m.id.toString() === malfunctionId);
    const device = !!selectedMalfunction
        ? props.devices.find((d) => d.device.device_id === selectedMalfunction.device_id)
        : undefined;

    useEffect(() => {
        props.setSelectionModel(selectedMalfunction ? [selectedMalfunction.id] : []);
        if (selectedMalfunction && !!props.gridApiRef?.current?.getRowNode) {
            const node = props.gridApiRef.current.getRowNode(selectedMalfunction.id);
            if (!!node && node.parent && !!props.gridApiRef.current.setRowChildrenExpansion) {
                props.gridApiRef.current.setRowChildrenExpansion(node.parent, true);
            }
        }
    }, [selectedMalfunction, props.gridApiRef]);

    if (!selectedMalfunction) {
        if (props.malfunctions.length > 0) {
            history.replace('/tasks');
        }
        return null;
    }

    return (
        <MalfunctionDetails
            gridApiRef={props.gridApiRef}
            malfunction={selectedMalfunction}
            vehicleMalfunctions={props.vehicleMalfunctions[selectedMalfunction.license_plate] || []}
            onClose={props.onClose}
            users={props.users}
            setAlert={props.setAlert}
            isLoadingUsers={props.isLoadingUsers}
            isErrorUsers={props.isErrorUsers}
            setAssignToGuest={props.setAssignToGuest}
            device={device}
            isMapView={props.isMapView}
        />
    );
};

const MalfunctionsComponent: React.FC = () => {
    const { api } = useApi();
    const { profile } = useProfile();
    const {
        data,
        isLoading: isLoadingMalfunctions,
        isFetching: isFetchingMalfunctions,
        isError: isErrorMalfunctions,
    } = useReactQuery({
        // eslint-disable-next-line @tanstack/query/exhaustive-deps
        queryKey: ['malfunctions'],
        queryFn: () => getMalfunctions(api),
        staleTime: MILLISECONDS_IN_SECOND * SECONDS_IN_MINUTE * 5,
    });
    const malfunctions = data || ([] as Malfunction[]);
    const isOEUser = OE_ROLES.includes(profile.role);
    const isAllowedCustomer = ALLOWED_CHANGE_VIN_CUSTOMERS.includes(profile.customer.name.toLowerCase());
    const canChangeVIN = isOEUser || isAllowedCustomer;
    const [aggregatedMalfunctions, setAggregatedMalfunctions] = useState<AggregatedMalfunctionsHistory>();
    const [isLoadingAggregatedMalfunctions, setIsLoadingAggregatedMalfunctions] = useState<boolean>(true);
    const [isErrorAggregatedMalfunctions, setIsErrorAggregatedMalfunctions] = useState<boolean>(false);
    const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
    const { devices, isLoading: isLoadingDevices, isError: isErrorDevices } = useDevices();
    const location = useLocation();
    const query = useQuery();

    const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
    const [searchText, setSearchText] = useState<string>('');
    const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
    const queryClient = useQueryClient();
    const { i18n, t } = useTranslation();
    const isMobile = useIsMobile();
    const locale = localeObjectMap[i18n.languages[0]];
    const history = useHistory();
    const [alertElement, setAlert] = useAlert();
    const [containerRef, node] = useCallbackRef();
    const listRef = useRef<VariableSizeList>(null);
    const [height, setHeight] = useState<number>(870);
    const [cellModesModel, setCellModesModel] = React.useState<GridCellModesModel>({});
    const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState<boolean>(false);
    const [isLoadingAssignee, setIsLoadingAssignee] = useState<boolean>(false);
    const [isSelectMode, setIsSelectMode] = useState<boolean>(false);
    const [assignToGuest, setAssignToGuest] = useState<number[]>([]);
    const initialGroupMalfunctions = getStoredValue<boolean>(GROUP_MALFUNCTIONS_KEY, '', IS_GROUPED_BY_DEFAULT);
    const [groupMalfunctions, setGroupMalfunctions] = useState<boolean>(initialGroupMalfunctions);
    const initialPrioritizeMalfunctions = getStoredValue<boolean>(
        PRIORITIZE_MALFUNCTION_KEY,
        '',
        IS_DEFAULT_PRIORITIZE,
    );
    const [prioritizeMalfunctions, setPrioritizeMalfunctions] = useState<boolean>(initialPrioritizeMalfunctions);
    const [rowGroupingModel, setRowGroupingModel] = useState<GridRowGroupingModel>(
        initialGroupMalfunctions ? ['license_plate'] : [],
    );
    const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(
        initialGroupMalfunctions ? { license_plate: false } : {},
    );
    const [expanded, setExpanded] = useState<string | false>(false);
    const [isMapView, setIsMapView] = useState<boolean>(false);
    const [isClustered, setIsClustered] = useState<boolean>(true);
    const { malfunctionId } = useParams<{ malfunctionId: string }>();

    useEffect(() => {
        const items: GridFilterModel['items'] = [];
        const licensePlate = query.get('license_plate');
        if (licensePlate) {
            items.push({ field: 'license_plate', operator: 'equals', value: licensePlate });
        }
        const malfunction = query.get('task_type');
        if (malfunction && Object.values(MalfunctionTypeEnum).includes(malfunction as MalfunctionTypeEnum)) {
            items.push({ field: 'malfunction', operator: 'is', value: malfunction as MalfunctionTypeEnum });
        }
        const subFleet = query.get('sub_fleet');
        if (subFleet) {
            items.push({ field: 'sub_fleet', operator: 'is', value: subFleet });
        }
        const assigneeParam = query.get('assignee');
        if (assigneeParam) {
            const assigneeId = parseInt(assigneeParam, 10);
            if (!Number.isNaN(assigneeId)) {
                items.push({ field: 'assignee', operator: 'is', value: assigneeId });
            }
        }
        setFilterModel((prev) => ({ ...prev, items }));
    }, []);

    useEffect(() => {
        if (filterModel === undefined) {
            return;
        }
        const params = new URLSearchParams();
        for (const item of filterModel.items || []) {
            if (item.field === 'license_plate' && item.value != null) {
                params.set('license_plate', String(item.value));
            } else if (item.field === 'malfunction' && item.value != null) {
                params.set('task_type', String(item.value));
            } else if (item.field === 'sub_fleet' && item.value != null) {
                params.set('sub_fleet', String(item.value));
            } else if (item.field === 'assignee' && item.value != null) {
                params.set('assignee', String(item.value));
            }
        }
        const search = params.toString();
        const desired = search ? `?${search}` : '';
        if (location.search !== desired) {
            history.replace({ pathname: location.pathname, search: desired });
        }
    }, [filterModel, location.pathname, location.search, history]);

    const selectedMalfunction = malfunctions.find((m) => m.id.toString() === malfunctionId);

    const selectionModelLeaves = selectionModel.filter((rowId) => !rowId.toString().includes(GROUP_ROW_ID_STR));

    const handleChangeExpanded = (panel: string) => (_event: React.SyntheticEvent, newExpanded: boolean) => {
        setExpanded(newExpanded ? panel : false);
    };

    const apiRef = useGridApiRef();

    const includesFinishedTasks = malfunctions.some((m) => !m.is_unwell);

    const { customerUsers, isLoading: isLoadingUsers, isError: isErrorUsers } = useCustomer(true);
    const usersById = customerUsers.reduce((acc, user) => {
        acc[user.user_id] = user;
        return acc;
    }, {} as Record<number, CustomerUserDetails>);

    useEffect(() => {
        document.title = `${DEFAULT_TITLE} | ${t(`navigator.${malfunctionsPageDefs.name}`)}`;
    }, []);

    useEffect(() => {
        if (!node) return;
        const resizeObserver = new ResizeObserver(() => {
            if (node.offsetHeight) {
                setHeight(node.offsetHeight);
            }
        });
        resizeObserver.observe(node);
        return () => resizeObserver.disconnect();
    }, [node]);

    useEffect(() => {
        if (listRef.current) {
            listRef.current.resetAfterIndex(0);
        }
    }, [searchText, filterModel]);

    useEffect(() => {
        setRowGroupingModel(groupMalfunctions ? ['license_plate'] : []);
        setColumnVisibilityModel((prev) => {
            return { ...prev, license_plate: !groupMalfunctions };
        });
    }, [groupMalfunctions]);

    const malfunctionsByLicensePlate = malfunctions.reduce((acc, m) => {
        const licensePlate = m.license_plate;
        if (acc[licensePlate] === undefined) {
            acc[licensePlate] = [m];
        } else {
            acc[licensePlate].push(m);
        }
        return acc;
    }, {} as { [licensePlate: string]: Malfunction[] });

    const getAggregatedMalfunctions = () => {
        setIsErrorAggregatedMalfunctions(false);
        setIsLoadingAggregatedMalfunctions(true);
        api.apiV0MalfunctionAggregatedGet()
            .then((res) => {
                setAggregatedMalfunctions(res.data);
            })
            .catch(() => setIsErrorAggregatedMalfunctions(true))
            .finally(() => setIsLoadingAggregatedMalfunctions(false));
    };

    useEffect(getAggregatedMalfunctions, []);

    const handleAssign = (malfunctionIds: number[], userId: number) => {
        setIsLoadingAssignee(true);
        return api
            .apiV0MalfunctionPatch({
                malfunctionAssigneeRequest: { malfunctions: malfunctionIds, assignee: userId },
            })
            .then(() => {
                apiRef.current.updateRows(malfunctionIds.map((rowId) => ({ id: rowId, assignee: userId })));
                queryClient.setQueryData<Malfunction[]>(['malfunctions'], (prev) => {
                    if (!!prev) {
                        return prev.map((malfunction) => {
                            if (malfunctionIds.includes(malfunction.id)) {
                                return {
                                    ...malfunction,
                                    assignee: userId,
                                };
                            }
                            return malfunction;
                        });
                    }
                });
                setIsAssignmentModalOpen(false);
            })
            .catch(() => {
                setAlert({
                    message: t(
                        `content.malfunctions.details.error_assigning${malfunctionIds.length > 1 ? '_plural' : ''}`,
                    ),
                    type: 'error',
                    duration: 6000,
                });
            });
    };

    const handleCellClick: GridEventListener<'cellClick'> = React.useCallback(
        (params: GridCellParams, event: React.MouseEvent) => {
            if (!params.isEditable) {
                return;
            }
            // Ignore portal
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if ((event.target as any).nodeType === 1 && !event.currentTarget.contains(event.target as Element)) {
                return;
            }
            event.stopPropagation();
            setCellModesModel((prevModel) => {
                return {
                    // Revert the mode of the other cells from other rows
                    ...Object.keys(prevModel).reduce(
                        (acc, id) => ({
                            ...acc,
                            [id]: Object.keys(prevModel[id]).reduce(
                                (acc2, field) => ({
                                    ...acc2,
                                    [field]: { mode: GridCellModes.View },
                                }),
                                {},
                            ),
                        }),
                        {},
                    ),
                    [params.id]: {
                        // Revert the mode of other cells in the same row
                        ...Object.keys(prevModel[params.id] || {}).reduce(
                            (acc, field) => ({ ...acc, [field]: { mode: GridCellModes.View } }),
                            {},
                        ),
                        [params.field]: { mode: GridCellModes.Edit },
                    },
                };
            });
        },
        [],
    );

    const handleCellModesModelChange = React.useCallback((newModel: GridCellModesModel) => {
        setCellModesModel(newModel);
    }, []);

    const AutocompleteInputCell = (props: GridRenderWithUserOptions) => {
        const [open, setOpen] = useState<boolean>(true);
        const { id, field, value } = props;
        const apiRef = useGridApiContext();

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const handleChange = async (_event: any, newValue: string) => {
            await apiRef.current.setEditCellValue({ id, field, value: newValue });
            apiRef.current.stopCellEditMode({ id, field });
            if (newValue?.toString() === '0') {
                setAssignToGuest([id as number]);
            } else {
                const assignee = newValue !== null ? parseInt(newValue) : null;
                api.apiV0MalfunctionPatch({
                    malfunctionAssigneeRequest: {
                        malfunctions: [props.row.id],
                        assignee,
                    },
                })
                    .then(() => {
                        queryClient.setQueryData<Malfunction[]>(['malfunctions'], (prev) => {
                            if (!!prev) {
                                return prev.map((malfunction) => {
                                    if (malfunction.id === props.row.id) {
                                        return {
                                            ...malfunction,
                                            assignee,
                                        };
                                    }
                                    return malfunction;
                                });
                            }
                            return prev;
                        });
                    })
                    .catch(() => {
                        setAlert({
                            message: t('content.malfunctions.details.error_assigning'),
                            type: 'error',
                            duration: 6000,
                        });
                        apiRef.current.updateRows([
                            { id: props.row.id, assignee: malfunctions.find((m) => m.id === id)?.assignee },
                        ]);
                    });
            }
        };

        return (
            <Autocomplete
                id={`malfunctions-data-grid-cell-${id}-${field}`}
                PopperComponent={CustomPopper}
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                fullWidth
                value={value || ''}
                isOptionEqualToValue={(option, value) => option === value || value === ''}
                onChange={handleChange}
                getOptionLabel={(option) => usersById[option]?.name || ''}
                renderOption={(props, option) => {
                    if (option === 0) {
                        return (
                            <li
                                id="guest-technician-option"
                                data-testid="guest-technician-option"
                                {...props}
                                key={option}
                                aria-selected={option === value}
                                style={{ height: '45px' }}
                            >
                                <BuildCircleIcon color="primary" sx={{ fontSize: 39.5, ml: '-3px', mr: '3px' }} />
                                <Typography variant="body2">
                                    {t('content.malfunctions.assign_to_guest.menu_item')}
                                </Typography>
                            </li>
                        );
                    }
                    const user = usersById[option];
                    return (
                        <li
                            id={`user-${user.user_id}-list-item`}
                            data-testid={`user-${user.user_id}-list-item`}
                            {...props}
                            key={user.user_id}
                            aria-selected={option === value}
                        >
                            {getUserAvatar(user)}
                            <Typography variant="body2">{user.name}</Typography>
                        </li>
                    );
                }}
                options={[
                    0,
                    ...props.options
                        .filter(
                            (u) =>
                                flattenRoles(malfunctionsPageDefs).includes(u.role) &&
                                (!props.row.malfunction.sub_fleet ||
                                    !u.sub_fleets ||
                                    u.sub_fleets.length === 0 ||
                                    u.sub_fleets.includes(props.row.malfunction.sub_fleet)),
                        )
                        .map((u) => u.user_id),
                ]}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        data-testid={'autocomplete-input'}
                        fullWidth
                        variant="standard"
                        InputProps={{
                            ...params.InputProps,
                            type: 'search',
                            disableUnderline: true,
                            // match input style with the cell
                            style: { paddingLeft: '10px', paddingRight: '9px', fontSize: '0.875rem' },
                        }}
                        sx={{
                            '& .MuiInputBase-root': {
                                height: '100%',
                            },
                        }}
                    />
                )}
                sx={{
                    height: '100%',
                    '& .MuiFormControl-root': { height: '100%' },
                    '& .MuiAutocomplete-endAdornment': { paddingRight: '12px' },
                }}
            />
        );
    };

    const renderAutocompleteEditAssignee = (params: GridRenderCellParams, users: CustomerUserDetails[]) => {
        return <AutocompleteInputCell options={users} {...params} />;
    };

    const plateToInternalId = useMemo(
        () => buildNormalizedPlateToInternalIdMap(devices.map((d) => d.device)),
        [devices],
    );

    const columns: GridColDef[] = useMemo(() => {
        const showVehicleInternalId = profile.customer.settings.show_vehicle_internal_id ?? false;
        const colDefs: GridColDef[] = [
            {
                field: 'license_plate',
                headerName: t('content.malfunctions.table.license_plate'),
                flex: 1,
                minWidth: 150,
                filterOperators: getGridStringOperators().filter((operator) => operator.value === 'equals'),
                renderCell: (params) => {
                    const plate = String(params.value ?? '');
                    const internalId = getInternalIdForNormalizedPlate(plateToInternalId, plate);
                    const display = formatLicensePlateWithInternalId(plate, internalId, showVehicleInternalId);
                    return <Typography fontSize={14}>{display}</Typography>;
                },
            },
            {
                field: 'malfunction',
                headerName: t('content.malfunctions.table.malfunction'),
                type: 'singleSelect',
                valueOptions: Object.values(MalfunctionTypeEnum).map((type) => ({
                    value: type,
                    label: t(`content.malfunctions.table.type.${type.toLocaleLowerCase()}`),
                })),
                valueFormatter: (params: GridValueFormatterParams) =>
                    !!params.value ? t(`content.malfunctions.table.type.${params.value.toLocaleLowerCase()}`) : '',
                flex: 1,
                minWidth: 150,
                filterOperators: getGridSingleSelectOperators().filter((operator) => operator.value === 'is'),
                renderCell: (params) => {
                    if (params.rowNode.type === 'group') {
                        const numberOfMalfunctions = apiRef.current.getRowGroupChildren({ groupId: params.id }).length;
                        return (
                            <Typography fontSize={14}>
                                {numberOfMalfunctions > 1
                                    ? t('content.malfunctions.malfunction_plural', { num: numberOfMalfunctions })
                                    : t('content.malfunctions.malfunction_singular')}
                            </Typography>
                        );
                    }
                    return (
                        <Typography fontSize={14}>
                            {!!params.value
                                ? t(`content.malfunctions.table.type.${params.value.toLocaleLowerCase()}`)
                                : ''}
                        </Typography>
                    );
                },
                sortComparator: groupMalfunctions
                    ? (_v1, _v2, cellParams1, cellParams2) => {
                          if (cellParams1.rowNode.type === 'group' && cellParams2.rowNode.type === 'group') {
                              if (cellParams1.rowNode.groupingField === cellParams2.rowNode.groupingField) {
                                  const filteredDescendantCountLookup =
                                      gridFilteredDescendantCountLookupSelector(apiRef);
                                  const cell1DescendantCount =
                                      filteredDescendantCountLookup[cellParams1.rowNode.id] ?? 0;
                                  const cell2DescendantCount =
                                      filteredDescendantCountLookup[cellParams2.rowNode.id] ?? 0;
                                  return cell1DescendantCount - cell2DescendantCount;
                              }
                              return 0;
                          }
                          if (cellParams1.rowNode.type === 'group') {
                              return 1;
                          }
                          if (cellParams2.rowNode.type === 'group') {
                              return -1;
                          }
                          return 0;
                      }
                    : gridStringOrNumberComparator,
            },
            {
                field: 'sub_fleet',
                headerName: t('content.malfunctions.table.sub_fleet'),
                type: 'singleSelect',
                valueOptions: profile.customer.sub_fleets,
                flex: 1,
                minWidth: 150,
                filterOperators: getGridSingleSelectOperators().filter((operator) => operator.value === 'is'),
                renderCell: (params) => {
                    if (params.rowNode.type === 'group') {
                        const firstChild = apiRef.current.getRowGroupChildren({ groupId: params.id })[0];
                        return (
                            <Typography fontSize={14}>{apiRef.current.getRow(firstChild).sub_fleet || ''}</Typography>
                        );
                    }
                    return <Typography fontSize={14}>{params.value}</Typography>;
                },
                sortable: !groupMalfunctions,
            },
            {
                field: 'assignee',
                headerName: t('content.malfunctions.table.assignee'),
                type: 'singleSelect',
                valueOptions: customerUsers.map((user) => ({
                    value: user.user_id,
                    label: user.name,
                })),
                flex: 1,
                minWidth: 150,
                editable: true,
                filterOperators: getGridSingleSelectOperators().filter((operator) => operator.value === 'is'),
                renderCell: (params) => {
                    let assignee;
                    if (params.rowNode.type === 'group') {
                        const children = apiRef.current.getRowGroupChildren({ groupId: params.id });
                        const assignees = children.map((child) => apiRef.current.getRow(child).assignee);
                        assignee = assignees.every((a) => a === assignees[0]) ? assignees[0] : null;
                    } else {
                        assignee = params.value;
                    }
                    if (assignee === 0) {
                        return (
                            <Box
                                id={`malfunction-${params.id}-assignee`}
                                data-testid={`malfunction-${params.id}-assignee`}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                <BuildCircleIcon color="primary" sx={{ fontSize: 39.5, ml: '-3px', mr: '3px' }} />
                                <Typography fontSize={14}>
                                    {t('content.malfunctions.assign_to_guest.menu_item')}
                                </Typography>
                            </Box>
                        );
                    }
                    if (!assignee || !usersById[assignee]) {
                        return null;
                    }
                    return (
                        <Box
                            id={`malfunction-${params.id}-assignee`}
                            data-testid={`malfunction-${params.id}-assignee`}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {getUserAvatar(usersById[assignee])}
                            <Typography fontSize={14} sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {usersById[assignee].name}
                            </Typography>
                        </Box>
                    );
                },
                renderEditCell: (params) => renderAutocompleteEditAssignee(params, customerUsers),
            },
            {
                field: 'since',
                headerName: t('content.malfunctions.table.since'),
                ...getDateTimeColumnType(locale),
                renderCell: (params) => {
                    if (params.rowNode.type === 'group') {
                        return null;
                    }
                    return dayjs(params.value as Date).format(profile.dateTimeFormat);
                },
                flex: 1,
                minWidth: 150,
            },
            {
                field: 'last_updated',
                headerName: t('content.malfunctions.table.last_updated'),
                ...getDateTimeColumnType(locale),
                renderCell: (params) => {
                    if (params.rowNode.type === 'group') {
                        return null;
                    }
                    return dayjs(params.value as Date).format(profile.dateTimeFormat);
                },
                flex: 1,
                minWidth: 150,
            },
            {
                field: 'comment_count',
                headerName: t('content.malfunctions.table.comments'),
                type: 'number',
                align: 'left',
                headerAlign: 'left',
                minWidth: 125,
                renderCell: (params) => {
                    let numberOfComments;
                    if (params.rowNode.type === 'group') {
                        numberOfComments = apiRef.current
                            .getRowGroupChildren({ groupId: params.id })
                            .reduce((acc, row) => acc + apiRef.current.getRow(row).comment_count, 0);
                    } else {
                        numberOfComments = params.value;
                    }
                    const hasComments = !!numberOfComments;
                    return (
                        <Box
                            id={`malfunction-${params.id}-comments`}
                            data-testid={`malfunction-${params.id}-comments`}
                            data-value={numberOfComments}
                            sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}
                        >
                            <ChatBubbleIcon
                                sx={{
                                    fontSize: 28,
                                    color: hasComments ? palette.secondary : palette.neutral[200],
                                }}
                            />
                            {!!numberOfComments ? (
                                <Box sx={{ position: 'absolute', top: 2.5, left: 3, width: 70 / 3 }}>
                                    <Typography fontSize={12} textAlign="center" fontWeight={500}>
                                        {numberOfComments}
                                    </Typography>
                                </Box>
                            ) : null}
                        </Box>
                    );
                },
                sortable: !groupMalfunctions,
            },
        ];
        if (includesFinishedTasks) {
            colDefs.push({
                field: 'fixed',
                headerName: t('content.malfunctions.table.fixed'),
                type: 'boolean',
            });
        }
        return colDefs;
    }, [profile, customerUsers, includesFinishedTasks, groupMalfunctions, apiRef, plateToInternalId, t]);

    const prioritizeByLicensePlate: Record<string, number> = useMemo(() => {
        const prioritizeByLicensePlate: Record<string, number> = {};

        for (const malfunction of malfunctions) {
            const license_plate = malfunction.license_plate;
            if (!prioritizeByLicensePlate[license_plate]) {
                prioritizeByLicensePlate[license_plate] = 0;
            }
            prioritizeByLicensePlate[license_plate] = Math.max(
                prioritizeByLicensePlate[license_plate],
                getMalfunctionPriority(malfunction.type),
            );
        }

        return prioritizeByLicensePlate;
    }, [malfunctions]);

    const filteredRows: MalfunctionRow[] = useMemo(() => {
        const rows = malfunctions.map((malfunction) => {
            return {
                id: malfunction.id,
                malfunction: malfunction.type,
                license_plate: malfunction.license_plate,
                sub_fleet: malfunction.sub_fleet || '',
                assignee: malfunction.assignee,
                since: dayjs.unix(malfunction.since).toDate(),
                last_updated: dayjs.unix(malfunction.updated_at).toDate(),
                comment_count: malfunction.comment_count,
                fixed: !malfunction.is_unwell,
            };
        });
        let filteredRows = rows;
        if (searchText) {
            filteredRows = rows.filter((row) => {
                const malfunctionMatch = t(`content.malfunctions.table.type.${row.malfunction.toLocaleLowerCase()}`)
                    .toLocaleLowerCase()
                    .includes(searchText.toLocaleLowerCase());
                const normalizedSearch = normalizeLicensePlate(searchText);
                const normalizedRowPlate = normalizeLicensePlate(row.license_plate);
                const licensePlateMatch = normalizedRowPlate.includes(normalizedSearch);
                const internalId = getInternalIdForNormalizedPlate(plateToInternalId, row.license_plate);
                const internalIdStr =
                    (profile.customer.settings.show_vehicle_internal_id ?? false) &&
                    internalId != null &&
                    internalId !== ''
                        ? String(internalId).toLocaleLowerCase().replaceAll('-', '')
                        : '';
                const internalIdMatch = !!internalIdStr && internalIdStr.includes(normalizedSearch);
                const subFleetMatch = row.sub_fleet.toLocaleLowerCase().includes(searchText.toLocaleLowerCase());
                return malfunctionMatch || licensePlateMatch || internalIdMatch || subFleetMatch;
            });
        }
        if (prioritizeMalfunctions) {
            filteredRows = filteredRows.filter(
                (row) => prioritizeByLicensePlate[row.license_plate] === getMalfunctionPriority(row.malfunction),
            );
        }
        return filteredRows;
    }, [
        malfunctions,
        searchText,
        prioritizeMalfunctions,
        prioritizeByLicensePlate,
        plateToInternalId,
        profile.customer.settings.show_vehicle_internal_id,
        t,
    ]);

    let filteredListRows = filteredRows;
    for (const filter of filterModel?.items ?? []) {
        if (['malfunction', 'license_plate', 'sub_fleet', 'assignee'].includes(filter.field)) {
            filteredListRows = filteredListRows.filter(
                (row) => row[filter.field as keyof MalfunctionRow] === filter.value,
            );
        }
    }

    const relevantLicensePlates = new Set(filteredListRows.map((row) => row.license_plate));
    const relevantDevices = devices.filter((device) => relevantLicensePlates.has(device.device.license_plate));

    const handleRefresh = () => {
        void queryClient.invalidateQueries(['malfunctions']);
    };

    let mainContent;
    if (isMapView) {
        if (isLoadingMalfunctions || isLoadingDevices) {
            mainContent = (
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <CircularProgress />
                </Box>
            );
        } else {
            mainContent = (
                <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
                    <OverviewMap
                        devices={relevantDevices}
                        onIconClick={(licensePlate: string) =>
                            history.push({
                                pathname: `/tasks/${malfunctionsByLicensePlate[licensePlate][0].id}`,
                                search: location.search,
                            })
                        }
                        selectedDeviceId={selectedMalfunction?.device_id || undefined}
                        isClustered={isClustered}
                    />
                    {isMobile ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-end',
                                position: 'absolute',
                                bottom: 15,
                                right: 15,
                            }}
                        >
                            <Fab
                                id="fab-marker-cluster"
                                data-testid="fab-marker-cluster"
                                size="medium"
                                color={isClustered ? 'secondary' : 'primary'}
                                onClick={() => setIsClustered((prev) => !prev)}
                                sx={{ mr: 1 }}
                            >
                                <WorkspacesIcon />
                            </Fab>
                            <Fab
                                id="fab-list-view"
                                data-testid="fab-list-view"
                                color="primary"
                                onClick={() => setIsMapView(false)}
                            >
                                <ListIcon />
                            </Fab>
                        </Box>
                    ) : (
                        <Button
                            id="btn-list-view"
                            data-testid="btn-list-view"
                            variant="contained"
                            size="small"
                            onClick={() => setIsMapView(false)}
                            sx={{ height: '34px', position: 'absolute', top: 10, right: 54, zIndex: 1001 }}
                            startIcon={<ListIcon />}
                        >
                            {t('content.malfunctions.list_view')}
                        </Button>
                    )}
                </Box>
            );
        }
    } else if (isMobile) {
        if (isLoadingMalfunctions || isLoadingUsers) {
            mainContent = (
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <CircularProgress />
                </Box>
            );
        } else {
            let list;
            if (filteredRows.length === 0) {
                list = (
                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: 2 }}>
                        <Typography variant="overline">
                            {isErrorMalfunctions
                                ? t('content.malfunctions.error_loading_malfunctions')
                                : t('content.malfunctions.no_malfunctions_found')}
                        </Typography>
                    </Box>
                );
            } else if (groupMalfunctions) {
                const malfunctionsByLicensePlate = filteredListRows.reduce((acc, row) => {
                    if (!acc[row.license_plate]) {
                        acc[row.license_plate] = [];
                    }
                    acc[row.license_plate].push(row);
                    return acc;
                }, {} as Record<string, MalfunctionRow[]>);
                list = Object.entries(malfunctionsByLicensePlate).map(([licensePlate, rows]) => (
                    <Accordion
                        expanded={expanded === licensePlate}
                        disableGutters
                        key={licensePlate}
                        TransitionProps={{ unmountOnExit: true }}
                        onChange={handleChangeExpanded(licensePlate)}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: palette.neutral[50] }}>
                            <Typography>{`${licensePlate} (${rows.length})`}</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0, backgroundColor: palette.bgColor }}>
                            {rows.map((row, idx) => {
                                return (
                                    <MalfunctionListRow
                                        key={row.id}
                                        index={idx}
                                        style={{}}
                                        data={{
                                            rows: rows,
                                            usersById,
                                            selectionModel,
                                            setSelectionModel,
                                            isSelectMode,
                                            setIsSelectMode,
                                            inAccordion: true,
                                        }}
                                    />
                                );
                            })}
                        </AccordionDetails>
                    </Accordion>
                ));
            } else {
                list = (
                    <VariableSizeList
                        height={height}
                        width="100%"
                        itemSize={() => 140}
                        itemCount={filteredListRows.length}
                        overscanCount={5}
                        itemData={{
                            rows: filteredListRows,
                            usersById,
                            selectionModel,
                            setSelectionModel,
                            isSelectMode,
                            setIsSelectMode,
                            inAccordion: false,
                        }}
                        ref={listRef}
                    >
                        {MalfunctionListRow}
                    </VariableSizeList>
                );
            }
            mainContent = (
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        position: 'relative',
                    }}
                >
                    <Box
                        sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 1000,
                            width: '100%',
                            backgroundColor: palette.bgColor,
                        }}
                    >
                        <MalfunctionsToolbarMobile
                            searchText={searchText}
                            setSearchText={setSearchText}
                            filterModel={filterModel}
                            setFilterModel={setFilterModel}
                            isFilterOpen={isFilterOpen}
                            setIsFilterOpen={setIsFilterOpen}
                            handleRefresh={handleRefresh}
                            canChangeVIN={canChangeVIN}
                            setIsAssignmentModalOpen={setIsAssignmentModalOpen}
                            setIsMapView={setIsMapView}
                            groupMalfunctions={groupMalfunctions}
                            setGroupMalfunctions={setGroupMalfunctions}
                            prioritizeMalfunctions={prioritizeMalfunctions}
                            setPrioritizeMalfunctions={setPrioritizeMalfunctions}
                            selectedMalfunctionIds={selectionModel as number[]}
                            isViewButtonEnabled
                        />
                    </Box>

                    <Box ref={containerRef} sx={{ width: '100%', height: '100%', minHeight: 0, position: 'relative' }}>
                        <Fade in={isFilterOpen}>
                            <Box
                                onClick={() => setIsFilterOpen(false)}
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor: alpha(palette.black, 0.25),
                                    position: 'absolute',
                                    top: 0,
                                    zIndex: 2,
                                }}
                            ></Box>
                        </Fade>
                        {list}
                        <MalfunctionsFilter
                            isOpen={isFilterOpen}
                            filterModel={filterModel ?? { items: [] }}
                            setFilterModel={setFilterModel}
                            licensePlates={devices.map((d) => d.device.license_plate)}
                            users={customerUsers.reduce((acc, user) => {
                                acc[user.user_id] = user;
                                return acc;
                            }, {} as Record<number, CustomerUserDetails>)}
                            onClose={() => setIsFilterOpen(false)}
                        />
                    </Box>
                </Box>
            );
        }
    } else {
        mainContent = (
            <Box sx={{ flex: 1, height: '100%', p: 2, minWidth: 0 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
                    <StyledDataGrid
                        initialState={{
                            rowGrouping: { model: groupMalfunctions ? ['license_plate'] : [] },
                            columns: {
                                columnVisibilityModel: {
                                    license_plate: !groupMalfunctions,
                                },
                            },
                        }}
                        apiRef={apiRef}
                        id="malfunctions-data-grid"
                        checkboxSelection
                        columns={columns}
                        columnBuffer={8}
                        rows={filteredRows}
                        density="compact"
                        loading={isLoadingMalfunctions || isFetchingMalfunctions || isLoadingUsers}
                        disableRowSelectionOnClick
                        disableMultipleRowSelection
                        showCellVerticalBorder
                        showColumnVerticalBorder
                        cellModesModel={cellModesModel}
                        rowGroupingModel={rowGroupingModel}
                        onRowGroupingModelChange={(model) => setRowGroupingModel(model)}
                        columnVisibilityModel={columnVisibilityModel}
                        onColumnVisibilityModelChange={(model) => setColumnVisibilityModel(model)}
                        onCellModesModelChange={handleCellModesModelChange}
                        onCellClick={handleCellClick}
                        rowSelectionModel={selectionModel}
                        onRowClick={(params) => {
                            const rowNode = apiRef.current.getRowNode(params.id);
                            if (rowNode && rowNode.type === 'group') {
                                apiRef.current.setRowChildrenExpansion(params.id, !rowNode.childrenExpanded);
                            } else {
                                setSelectionModel([params.id]);
                                history.push({ pathname: `/tasks/${params.id}`, search: location.search });
                            }
                        }}
                        onRowSelectionModelChange={(newSelection) => {
                            // handle selection or deselection of parent rows
                            setSelectionModel((prev) => {
                                const oldParentsSet = new Set(
                                    prev.filter((id) => id.toString().includes(GROUP_ROW_ID_STR)),
                                );
                                const newParentsSet = new Set(
                                    newSelection.filter((id) => id.toString().includes(GROUP_ROW_ID_STR)),
                                );

                                const removedParents = [...oldParentsSet].filter((id) => !newParentsSet.has(id));
                                const addedParents = [...newParentsSet].filter((id) => !oldParentsSet.has(id));

                                const rowIdsToRemove = new Set();
                                removedParents.forEach((id) => {
                                    const rowIds = apiRef.current.getRowGroupChildren({ groupId: id });
                                    rowIds.forEach((rowId) => rowIdsToRemove.add(rowId));
                                });

                                const rowIdsToAdd: GridRowId[] = [];
                                addedParents.forEach((id) => {
                                    const rowIds = apiRef.current.getRowGroupChildren({ groupId: id });
                                    rowIdsToAdd.push(...rowIds);
                                });

                                newSelection = newSelection.filter((id) => !rowIdsToRemove.has(id));
                                newSelection.push(...rowIdsToAdd);

                                return newSelection;
                            });
                            if (newSelection.length === 1 && !newSelection[0].toString().includes(GROUP_ROW_ID_STR)) {
                                history.push({
                                    pathname: `/tasks/${newSelection[0]}`,
                                    search: location.search,
                                });
                            } else {
                                history.push({ pathname: '/tasks', search: location.search });
                            }
                        }}
                        groupingColDef={{ hideDescendantCount: true }}
                        filterModel={filterModel ?? { items: [] }}
                        onFilterModelChange={(newModel) => {
                            setFilterModel(newModel);
                        }}
                        localeText={
                            {
                                ...gridLocalization[i18n.languages[0]],
                                filterPanelRemoveAll: t('table.remove_all'),
                                columnMenuManageColumns: t('table.manage_columns'),
                                unpin: t('table.unpin'),
                                filterOperatorOnDate: t('table.on_date'),
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            } as any
                        }
                        slots={{
                            loadingOverlay: LinearProgress,
                            noRowsOverlay: () => (
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Typography variant="overline">
                                        {isErrorMalfunctions
                                            ? t('content.malfunctions.error_loading_malfunctions')
                                            : t('content.malfunctions.no_malfunctions_found')}
                                    </Typography>
                                </Box>
                            ),
                            toolbar: QuickSearchToolbar,
                        }}
                        slotProps={{
                            filterPanel: {
                                filterFormProps: {
                                    filterColumns: filterColumns,
                                    deleteIconProps: { id: 'delete-icon' },
                                    columnInputProps: { id: 'column-input' },
                                    operatorInputProps: { id: 'operator-input' },
                                    valueInputProps: { InputComponentProps: { id: 'value-input' } },
                                    logicOperatorInputProps: { sx: { display: 'none' } },
                                },
                                logicOperators: [GridLogicOperator.And],
                                getColumnForNewFilter,
                            },
                            toolbar: {
                                searchText,
                                setSearchText,
                                onRefresh: handleRefresh,
                                selectedMalfunctionIds: selectionModel,
                                onAssign: () => setIsAssignmentModalOpen(true),
                                groupMalfunctions,
                                setGroupMalfunctions,
                                prioritizeMalfunctions,
                                setPrioritizeMalfunctions,
                                setIsMapView,
                                isViewButtonEnabled: relevantDevices.some((device) => isLocationValid(device.location)),
                                canChangeVIN,
                            },
                        }}
                        disableVirtualization={isMobile || process.env.JEST_WORKER_ID !== undefined}
                        sx={{
                            height: '100%',
                            width: '100%',
                            maxWidth: '1200px',
                            '& .MuiDataGrid-row:hover': {
                                cursor: 'pointer',
                            },
                            '& .MuiDataGrid-cell:focus-within': {
                                outline: 'none',
                            },
                            backgroundColor: palette.neutral[50],
                            '& .MuiDataGrid-overlayWrapperInner': {
                                height: isLoadingMalfunctions || isFetchingMalfunctions ? '4px !important' : undefined,
                            },
                            '& .MuiDataGrid-groupingCriteriaCellToggle': {
                                margin: '2px',
                                marginLeft: '-6px',
                            },
                        }}
                    />
                </LocalizationProvider>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', height: '100%', display: 'flex' }}>
            {alertElement}
            {isAssignmentModalOpen ? (
                <AssignmentModal
                    open={isAssignmentModalOpen}
                    onClose={() => setIsAssignmentModalOpen(false)}
                    users={customerUsers}
                    selectedMalfunctions={malfunctions.filter((m) => selectionModelLeaves.includes(m.id))}
                    onAssign={async (userId: number) => {
                        await handleAssign(selectionModelLeaves as number[], userId);
                        setIsLoadingAssignee(false);
                    }}
                    isLoading={isLoadingAssignee}
                    setAssignToGuest={setAssignToGuest}
                />
            ) : null}
            {assignToGuest.length > 0 ? (
                <AssignGuestTechnicianModal
                    isLoading={isLoadingAssignee}
                    setIsLoading={setIsLoadingAssignee}
                    assignedMalfunctionIds={assignToGuest}
                    onAssign={(assigneeId: number) => {
                        handleAssign(assignToGuest, assigneeId)
                            .then(() => setAssignToGuest([]))
                            .finally(() => setIsLoadingAssignee(false));
                    }}
                    onClose={() => {
                        setAssignToGuest((prev) => {
                            apiRef.current.updateRows(prev.map((id) => ({ id, assignee: null })));
                            return [];
                        });
                    }}
                    setAlert={setAlert}
                />
            ) : null}
            {mainContent}
            <Switch>
                <Route path={'/tasks/verify'}>
                    <SetupTool
                        key="verify-setup-tool"
                        mode="verify"
                        dashboardLink={<DashboardLink malfunctionId={selectionModel[0]} />}
                    />
                </Route>
                <Route path={'/tasks/adjust'}>
                    <SetupTool key="adjust-setup-tool" mode="adjust" dashboardLink={<DashboardLink />} />
                </Route>
                {canChangeVIN ? (
                    <Route path={'/tasks/change-vin'}>
                        <ChangeVIN dashboardLink={<DashboardLink />} />
                    </Route>
                ) : null}
                <Route path={`/tasks/:malfunctionId/:modeStr?`}>
                    <MalfunctionDetailsWrapper
                        gridApiRef={isMobile ? undefined : apiRef}
                        key={`malfunction-details-${selectionModel[0]}`}
                        malfunctions={malfunctions}
                        vehicleMalfunctions={malfunctionsByLicensePlate}
                        onClose={() => setSelectionModel([])}
                        setSelectionModel={setSelectionModel}
                        users={customerUsers}
                        setAlert={setAlert}
                        isLoadingUsers={isLoadingUsers}
                        isErrorUsers={isErrorUsers}
                        setAssignToGuest={setAssignToGuest}
                        devices={devices}
                        isMapView={isMapView}
                    />
                </Route>
            </Switch>
            {isMobile ? undefined : (
                <MalfunctionsOverview
                    malfunctions={malfunctions}
                    aggregatedMalfunctions={aggregatedMalfunctions}
                    devices={devices}
                    isLoading={isLoadingMalfunctions || isLoadingDevices}
                    isLoadingAggregated={isLoadingAggregatedMalfunctions}
                    isError={isErrorMalfunctions || isErrorDevices}
                    isErrorAggregated={isErrorAggregatedMalfunctions}
                    onRetry={() => {
                        if (isErrorMalfunctions) {
                            queryClient.invalidateQueries(['malfunctions']);
                        }
                        if (isErrorAggregatedMalfunctions) {
                            getAggregatedMalfunctions();
                        }
                        if (isErrorDevices) {
                            queryClient.invalidateQueries(['devices']);
                        }
                    }}
                    setFilterModel={(type: MalfunctionTypeEnum) => {
                        setSelectionModel([]);
                        setFilterModel({
                            items: [{ field: 'malfunction', operator: 'is', value: type }],
                        });
                    }}
                />
            )}
        </Box>
    );
};

export default MalfunctionsComponent;
